import { CalendarCheck, Calendar, Palmtree, Hourglass } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatNumber } from '../../utils/calculations';

interface VacationKPICardsProps {
  earnedToDate: number;
  totalEntitlement: number;
  vacationDaysTaken: number;
  remainingVacation: number;
  carryOverDays: number;
  carryOverExpired?: boolean;
  adjustmentDays?: number;
  compact?: boolean;
}

const colorMap = {
  blue: {
    border: 'border-l-blue-500',
    iconBg: 'bg-blue-50 dark:bg-blue-900/30',
    iconText: 'text-blue-600 dark:text-blue-400',
  },
  indigo: {
    border: 'border-l-indigo-500',
    iconBg: 'bg-indigo-50 dark:bg-indigo-900/30',
    iconText: 'text-indigo-600 dark:text-indigo-400',
  },
  amber: {
    border: 'border-l-amber-500',
    iconBg: 'bg-amber-50 dark:bg-amber-900/30',
    iconText: 'text-amber-600 dark:text-amber-400',
  },
  green: {
    border: 'border-l-green-500',
    iconBg: 'bg-green-50 dark:bg-green-900/30',
    iconText: 'text-green-600 dark:text-green-400',
  },
  red: {
    border: 'border-l-red-500',
    iconBg: 'bg-red-50 dark:bg-red-900/30',
    iconText: 'text-red-600 dark:text-red-400',
  },
};

interface KPICardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  color: keyof typeof colorMap;
  compact?: boolean;
  highlight?: boolean;
}

function KPICard({ icon, label, value, sublabel, color, compact, highlight }: KPICardProps) {
  const c = colorMap[color];

  if (compact) {
    return (
      <Card className={cn('border', highlight && 'ring-1 ring-gray-900/10 dark:ring-white/20')}>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <div className={cn('rounded p-1', c.iconBg)}>
              <span className={c.iconText}>{icon}</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
              <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-gray-50">{value}</p>
              {sublabel && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{sublabel}</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border-l-4', c.border, highlight && 'ring-1 ring-gray-900/10 dark:ring-white/20')}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn('rounded-md p-1.5', c.iconBg)}>
            <span className={c.iconText}>{icon}</span>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-50">{value}</p>
            {sublabel && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sublabel}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function buildCarryOverSublabel(days: number, expired?: boolean): string {
  if (days <= 0) return '';
  if (expired) return `(${formatNumber(days, 1)} Übertrag verfallen)`;
  return `+${formatNumber(days, 1)} Übertrag`;
}

function buildAdjustmentSublabel(days: number | undefined): string {
  if (!days || days === 0) return '';
  const sign = days > 0 ? '+' : '';
  return `${sign}${formatNumber(days, 1)} Anpassung`;
}

export function VacationKPICards({
  earnedToDate,
  totalEntitlement,
  vacationDaysTaken,
  remainingVacation,
  carryOverDays,
  carryOverExpired,
  adjustmentDays,
  compact,
}: VacationKPICardsProps) {
  const iconSize = compact ? 'h-3.5 w-3.5' : 'h-4 w-4';

  // Sublabel für Jahresanspruch: Übertrag + Anpassungen
  const parts: string[] = [];
  const coLabel = buildCarryOverSublabel(carryOverDays, carryOverExpired);
  if (coLabel) parts.push(coLabel);
  const adjLabel = buildAdjustmentSublabel(adjustmentDays);
  if (adjLabel) parts.push(adjLabel);
  const entitlementSublabel = parts.length > 0 ? parts.join(', ') : undefined;

  // Sublabel für Erdient: Übertrag wenn vorhanden und nicht verfallen
  const earnedSublabel = carryOverDays > 0 && !carryOverExpired
    ? `+${formatNumber(carryOverDays, 1)} Übertrag`
    : undefined;

  const remainingColor = remainingVacation > 0 ? 'green' : 'red';

  return (
    <div className={cn(
      'grid grid-cols-2 lg:grid-cols-4',
      compact ? 'gap-3' : 'gap-4'
    )}>
      <KPICard
        icon={<CalendarCheck className={iconSize} />}
        label="Erdient bis heute"
        value={`${formatNumber(earnedToDate, 1)} Tage`}
        sublabel={earnedSublabel}
        color="blue"
        compact={compact}
      />
      <KPICard
        icon={<Calendar className={iconSize} />}
        label="Jahresanspruch"
        value={`${formatNumber(totalEntitlement, 1)} Tage`}
        sublabel={entitlementSublabel}
        color="indigo"
        compact={compact}
      />
      <KPICard
        icon={<Palmtree className={iconSize} />}
        label="Genommen"
        value={`${formatNumber(vacationDaysTaken, 1)} Tage`}
        sublabel={`von ${formatNumber(totalEntitlement, 1)}`}
        color="amber"
        compact={compact}
      />
      <KPICard
        icon={<Hourglass className={iconSize} />}
        label="Verbleibend"
        value={`${formatNumber(remainingVacation, 1)} Tage`}
        color={remainingColor}
        compact={compact}
        highlight
      />
    </div>
  );
}
